/**
 * Standalone code-gated routes (e.g. /gogoprop).
 *
 * Production: set REACT_APP_GOGOPROP_ACCESS_CODE in the build environment.
 * Policy: at least 12 characters, and include at least one letter, one digit, and one non-alphanumeric symbol.
 * The React route gate is UX-only; it does not secure Firestore. See research doc linked in docs/operations/security/.
 */

import { logger } from '../utils/logger';

export type StandaloneStorageMode = 'local' | 'session';

export interface StandaloneCodeGateConfig {
  route: string;
  accessCode: string;
  storageKey: string;
  storageMode: StandaloneStorageMode;
}

const MIN_STANDALONE_CODE_LENGTH = 12;

/** Local dev only: meets policy so `npm start` works without .env. Never relied on in production builds. */
const DEV_ONLY_FALLBACK_CODE = 'DevGoGoProp#01';

const INVISIBLE_CHARS = /[\u200B-\u200D\uFEFF]/g;

/**
 * Normalize user input or env values for comparison (trim, NFC, strip invisible chars).
 */
export function normalizeStandaloneCodeInput(value: string): string {
  return value.replace(INVISIBLE_CHARS, '').normalize('NFC').trim();
}

export function isStandaloneAccessCodePolicyCompliant(code: string): boolean {
  const normalized = normalizeStandaloneCodeInput(code);
  if (normalized.length < MIN_STANDALONE_CODE_LENGTH) return false;
  const hasLetter = /[A-Za-z]/.test(normalized);
  const hasDigit = /\d/.test(normalized);
  const hasSymbol = /[^A-Za-z0-9]/.test(normalized);
  return hasLetter && hasDigit && hasSymbol;
}

function resolveGoGoPropAccessCode(): string {
  const raw = process.env.REACT_APP_GOGOPROP_ACCESS_CODE ?? '';
  const trimmed = normalizeStandaloneCodeInput(raw);

  if (isStandaloneAccessCodePolicyCompliant(trimmed)) {
    return trimmed;
  }

  if (process.env.NODE_ENV === 'development') {
    if (raw && !isStandaloneAccessCodePolicyCompliant(trimmed)) {
      logger.warn(
        '[standaloneAccess] REACT_APP_GOGOPROP_ACCESS_CODE does not meet policy (12+ chars with letter, digit, symbol). Using dev-only fallback.'
      );
    } else if (!trimmed) {
      logger.warn(
        '[standaloneAccess] REACT_APP_GOGOPROP_ACCESS_CODE is unset; using dev-only fallback. Configure env before production deploy.'
      );
    }
    return DEV_ONLY_FALLBACK_CODE;
  }

  // Production (and other non-dev builds): never ship a guessable default.
  if (raw && !isStandaloneAccessCodePolicyCompliant(trimmed)) {
    logger.warn(
      '[standaloneAccess] REACT_APP_GOGOPROP_ACCESS_CODE is set but rejected by policy. Non-admin access to /gogoprop is disabled until fixed.'
    );
  }

  return '';
}

export const STANDALONE_PAGE_ACCESS = {
  gogoprop: {
    route: '/gogoprop',
    accessCode: resolveGoGoPropAccessCode(),
    storageKey: 'voluntas.standalone.gogoprop.unlocked',
    storageMode: 'local',
  } satisfies StandaloneCodeGateConfig,
} as const;
